import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/query";
import { AuthProvider, useAuth } from "./lib/auth";
import { api } from "./lib/api";
import { LoginPage } from "./pages/auth/LoginPage";
import { InloggenPage } from "./pages/auth/InloggenPage";
import { OnboardingPage } from "./pages/auth/OnboardingPage";
import { AdminShell } from "./components/admin/AdminShell";
import { GeenToegang } from "./components/admin/GeenToegang";
import { canAccess, firstAccessiblePath, type AdminAccess } from "./lib/access";
import { PreviewModeProvider } from "./lib/preview";
import { PreviewBanner } from "./components/school-portal/PreviewBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Admin pages — the portal-style admin has exactly two pages (Scholen + Aanbod). The
// activity editor is reachable from Aanbod via "Bewerken".
import { ScholenConsolePage } from "./pages/admin/ScholenConsolePage";
import { AdminSchoolDetailPage } from "./pages/admin/SchoolDetailPage";
import { AanbodPage } from "./pages/admin/AanbodPage";
import { InstellingenPage } from "./pages/admin/InstellingenPage";
import { BackupsPage } from "./pages/admin/BackupsPage";
import { ActiviteitPage, AuditPage } from "./pages/admin/LogsPage";
import { ActivityEditPage } from "./pages/admin/ActivityEditPage";
import { TeamMemberPage } from "./pages/admin/TeamMemberPage";
import { ProfilePage } from "./pages/admin/ProfilePage";
import { KluisPage } from "./pages/admin/KluisPage";
import { OverzichtPage } from "./pages/admin/OverzichtPage";

// School pages
import { SchoolPortalPage } from "./pages/school/PortalPage";
import { ProgrammaPage } from "./pages/school/ProgrammaPage";
import { SchoolOverviewPage } from "./pages/school/OverviewPage";
import { ContactPage } from "./pages/school/ContactPage";

// Common pages
import { NotificationsPage } from "./pages/NotificationsPage";
import { PrivacyPage } from "./pages/PrivacyPage";

// Requires an authenticated, onboarded admin/employee. APIs are server-protected; this
// keeps non-staff out of the admin shell entirely. The admin pages render their own
// AdminShell (matching the school portal), so this guard adds no layout of its own.
function AdminRoute({ children, access }: { children: React.ReactNode; access?: AdminAccess }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Laden...</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;

  const isStaff = user.roles.includes("admin") || user.roles.includes("employee");
  if (!isStaff) return <Navigate to="/" replace />;

  // Capability gate (defense in depth; the server enforces the same). Render a clear "no access"
  // view inside the shell rather than letting the page show blank/error states from 403'd fetches.
  if (access && !canAccess(access, user.roles, user.capabilities)) {
    return (
      <AdminShell>
        <GeenToegang resource={access} />
      </AdminShell>
    );
  }

  return <>{children}</>;
}

/** /admin index: send staff to their first accessible section (or their profile). */
function AdminIndexRedirect() {
  const { user } = useAuth();
  const to = user ? firstAccessiblePath(user.roles, user.capabilities) : "/admin/profiel";
  return <Navigate to={to} replace />;
}

/**
 * /admin/scholen/:seg — the console base hosts both school dossiers and dataset views under one
 * segment. A numeric segment is a school id (the dossier); a word is a dataset slug (the console,
 * e.g. /admin/scholen/contacten). This avoids a separate route base while keeping deep links clean.
 */
function ScholenSegmentRoute() {
  const { seg } = useParams<{ seg: string }>();
  if (seg && /^\d+$/.test(seg)) {
    return (
      <AdminRoute access="scholen">
        <AdminShell>
          <AdminSchoolDetailPage />
        </AdminShell>
      </AdminRoute>
    );
  }
  return (
    <AdminRoute access={["scholen", "inschrijvingen"]}>
      <ScholenConsolePage />
    </AdminRoute>
  );
}

function OnboardingRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Laden...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.onboarded) {
    return <Navigate to="/" replace />;
  }

  return <OnboardingPage />;
}

function ProtectedRouteNoLayout({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Laden...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.onboarded) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * For /school/:schoolId/* pages: if a school user lands on a school they have no access to
 * (e.g. a deleted school or someone else's id), send them home instead of showing the
 * "Kan de gegevens niet laden" error from the 403'd section fetches. Renders the page immediately
 * and only redirects once /my-schools confirms the school is not theirs, so valid pages never
 * flash a loader.
 *
 * KUVO staff normally never enter a school's own portal (they work in the admin dossier) and are
 * redirected to /admin/scholen. The exception is read-only preview: a staff member with
 * canPreviewSchools may open any school's portal, rendered in read-only mode (banner + disabled
 * inputs; the server enforces GET-only). Real coordinators are unaffected.
 */
function SchoolAccessGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { schoolId } = useParams<{ schoolId: string }>();
  const isStaff = user?.roles.includes("admin") || user?.roles.includes("employee");
  const canPreview = isStaff && !!user?.canPreviewSchools;
  const [deny, setDeny] = useState(false);

  useEffect(() => {
    if (isStaff) return;
    let active = true;
    setDeny(false);
    (async () => {
      try {
        const res = await (api.api.school as any)["my-schools"].$get();
        if (res.ok) {
          const data = await res.json();
          const schools = (data as any).schools ?? [];
          if (active && !schools.some((s: any) => s.schoolId === Number(schoolId))) setDeny(true);
        }
      } catch {}
    })();
    return () => {
      active = false;
    };
  }, [schoolId, isStaff]);

  // Staff without preview rights work in the admin dossier, not inside a school's portal.
  if (isStaff && !canPreview) return <Navigate to="/admin/scholen" replace />;
  if (deny) return <Navigate to="/" replace />;
  // Staff with preview: render the school portal in read-only preview mode.
  if (canPreview) {
    return (
      <PreviewModeProvider>
        <PreviewBanner />
        {children}
      </PreviewModeProvider>
    );
  }
  return <>{children}</>;
}

/** Auth + school-access guard for the /school/:schoolId/* content pages. */
function SchoolRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRouteNoLayout>
      <SchoolAccessGuard>{children}</SchoolAccessGuard>
    </ProtectedRouteNoLayout>
  );
}

/** Redirect old /school/:id/xxx routes to portal equivalents */
function SchoolLegacyRedirect({ section }: { section: string }) {
  const { schoolId } = useParams<{ schoolId: string }>();
  return <Navigate to={`/school/${schoolId}/schoolgegevens/${section}`} replace />;
}

/** Redirect a renamed flow URL (old slug → new slug), preserving any sub-section. */
function FlowSlugRedirect({ to }: { to: "schoolgegevens" | "programmaselectie" }) {
  const { schoolId, section } = useParams<{ schoolId: string; section?: string }>();
  const suffix = section ? `/${section}` : "";
  return <Navigate to={`/school/${schoolId}/${to}${suffix}`} replace />;
}

function SchoolHomeRedirect() {
  const [loading, setLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMySchools() {
      try {
        const res = await (api.api.school as any)["my-schools"].$get();
        if (res.ok) {
          const data = await res.json();
          const schools = (data as any).schools ?? [];
          if (schools.length > 0) {
            setRedirectTo(`/school/${schools[0].schoolId}/overzicht`);
          }
        }
      } catch {}
      setLoading(false);
    }
    fetchMySchools();
  }, []);

  if (loading) return <p className="text-muted-foreground">Laden...</p>;
  if (redirectTo) return <Navigate to={redirectTo} replace />;
  return <p className="text-muted-foreground">Geen school gevonden. Neem contact op met de beheerder.</p>;
}

/** Root entry: staff land on the admin overview, schools on their portal. */
function HomeRedirect() {
  const { user } = useAuth();
  const isStaff = user?.roles.includes("admin") || user?.roles.includes("employee");
  if (isStaff && user) return <Navigate to={firstAccessiblePath(user.roles, user.capabilities)} replace />;
  return <SchoolHomeRedirect />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      {/* Magic-link interstitial: POSTs the token to consume it (scanners only GET). */}
      <Route path="/inloggen" element={<InloggenPage />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/privacyverklaring" element={<PrivacyPage />} />

      {/* Admin routes — exactly two pages, plus the activity editor reached from Aanbod */}
      <Route path="/admin" element={<AdminRoute><AdminIndexRedirect /></AdminRoute>} />
      <Route path="/admin/overzicht" element={<AdminRoute access="overzicht"><OverzichtPage /></AdminRoute>} />
      <Route path="/admin/scholen" element={<AdminRoute access={["scholen", "inschrijvingen"]}><ScholenConsolePage /></AdminRoute>} />
      {/* One segment: numeric = school dossier, word = console dataset (e.g. /admin/scholen/contacten). */}
      <Route path="/admin/scholen/:seg" element={<ScholenSegmentRoute />} />
      <Route path="/admin/aanbod" element={<AdminRoute access="aanbod"><AanbodPage /></AdminRoute>} />
      {/* Inschrijvingen folded into the Scholen data-console (Inschrijvingen + Activiteiten datasets). */}
      <Route path="/admin/inschrijvingen" element={<Navigate to="/admin/scholen" replace />} />
      {/* Instellingen merges the old Configuratie + Team pages; the old paths redirect in. */}
      <Route path="/admin/instellingen" element={<AdminRoute access="configuratie"><InstellingenPage /></AdminRoute>} />
      <Route path="/admin/instellingen/:section" element={<AdminRoute access="configuratie"><InstellingenPage /></AdminRoute>} />
      <Route path="/admin/configuratie" element={<Navigate to="/admin/instellingen" replace />} />
      <Route path="/admin/team" element={<Navigate to="/admin/instellingen" replace />} />
      <Route path="/admin/backups" element={<AdminRoute access="backups"><BackupsPage /></AdminRoute>} />
      <Route path="/admin/logs" element={<AdminRoute access="audit"><ActiviteitPage /></AdminRoute>} />
      <Route path="/admin/audit" element={<AdminRoute access="audit"><AuditPage /></AdminRoute>} />
      <Route path="/admin/team/:userId" element={<AdminRoute access="adminOnly"><AdminShell><TeamMemberPage /></AdminShell></AdminRoute>} />
      <Route path="/admin/profiel" element={<AdminRoute><AdminShell><ProfilePage /></AdminShell></AdminRoute>} />
      {/* Kluis: personal client-side-encrypted vault; any staff member has their own. */}
      <Route path="/admin/kluis" element={<AdminRoute access="always"><KluisPage /></AdminRoute>} />
      <Route
        path="/admin/programmes/:id/edit"
        element={<AdminRoute access="aanbod"><AdminShell><ActivityEditPage /></AdminShell></AdminRoute>}
      />

      {/* School portal — overview */}
      <Route path="/school/:schoolId/overzicht" element={<SchoolRoute><SchoolOverviewPage /></SchoolRoute>} />

      {/* School portal — guided flow */}
      <Route path="/school/:schoolId/schoolgegevens" element={<SchoolRoute><SchoolPortalPage /></SchoolRoute>} />
      <Route path="/school/:schoolId/schoolgegevens/:section" element={<SchoolRoute><SchoolPortalPage /></SchoolRoute>} />

      {/* School portal — programme selection */}
      <Route path="/school/:schoolId/programmaselectie" element={<SchoolRoute><ProgrammaPage /></SchoolRoute>} />
      <Route path="/school/:schoolId/programmaselectie/:section" element={<SchoolRoute><ProgrammaPage /></SchoolRoute>} />

      {/* School portal — KUVO contact details */}
      <Route path="/school/:schoolId/contact" element={<SchoolRoute><ContactPage /></SchoolRoute>} />

      {/* Renamed flow slugs → redirect old URLs so existing links don't 404 */}
      <Route path="/school/:schoolId/gegevens" element={<ProtectedRouteNoLayout><FlowSlugRedirect to="schoolgegevens" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/gegevens/:section" element={<ProtectedRouteNoLayout><FlowSlugRedirect to="schoolgegevens" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/programma" element={<ProtectedRouteNoLayout><FlowSlugRedirect to="programmaselectie" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/programma/:section" element={<ProtectedRouteNoLayout><FlowSlugRedirect to="programmaselectie" /></ProtectedRouteNoLayout>} />

      {/* Legacy school routes → redirect to portal */}
      <Route path="/school/:schoolId" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="contactgegevens" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/info" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="contactgegevens" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/classes" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="groepen" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/constraints" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="studiedagen" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/selections" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="contactgegevens" /></ProtectedRouteNoLayout>} />

      {/* Renamed gegevens sub-section: old contactpersonen slug → contactgegevens */}
      <Route path="/school/:schoolId/schoolgegevens/contactpersonen" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="contactgegevens" /></ProtectedRouteNoLayout>} />

      {/* Renamed gegevens sub-section: old sluitingsdagen slug → studiedagen */}
      <Route path="/school/:schoolId/schoolgegevens/sluitingsdagen" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="studiedagen" /></ProtectedRouteNoLayout>} />

      {/* Removed gegevens sub-sections: vrije dagdelen + vakdocenten now live per group in the
          Groepen dialog, so old direct links go to Groepen instead of 404'ing. */}
      <Route path="/school/:schoolId/schoolgegevens/vrijedagdelen" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="groepen" /></ProtectedRouteNoLayout>} />
      <Route path="/school/:schoolId/schoolgegevens/vakdocenten" element={<ProtectedRouteNoLayout><SchoolLegacyRedirect section="groepen" /></ProtectedRouteNoLayout>} />

      {/* /notifications is an admin/KUVO feature; schools have no in-app inbox. */}
      <Route path="/notifications" element={<AdminRoute><AdminShell><NotificationsPage /></AdminShell></AdminRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<ProtectedRouteNoLayout><HomeRedirect /></ProtectedRouteNoLayout>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          {/* Defense in depth: a render throw anywhere in the tree degrades to a recoverable
              error screen instead of a frozen/blank portal. Sits inside the providers so the
              recovered tree still has query + auth context when "Opnieuw proberen" re-mounts it. */}
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
